import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/seo/SEOHead';
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from '../components/ui/accordion';
import { ArrowRight, XCircle, AlertCircle, Globe, Clock, CheckCircle, Shield, Users } from 'lucide-react';

const faqs = [
  {
    question: "What makes someone inadmissible to the United States?",
    answer: "Inadmissibility can result from various factors including criminal convictions (even minor offences like DUI), immigration violations, certain health conditions, or security concerns. Even a single criminal conviction from decades ago can trigger inadmissibility under U.S. immigration law."
  },
  {
    question: "How long is a U.S. Entry Waiver valid?",
    answer: "U.S. Entry Waivers are typically granted for periods of 1-5 years, depending on the circumstances of the case. The waiver must be renewed before expiration to maintain legal entry status."
  },
  {
    question: "Can I apply for a waiver if I've been denied entry before?",
    answer: "Yes, prior denials of entry do not automatically disqualify you from obtaining a waiver. However, the denial history will be considered as part of your application. Our team can help prepare documentation addressing previous denials."
  },
  {
    question: "How long does the waiver application process take?",
    answer: "Processing times vary significantly, typically ranging from 6-18 months. Factors affecting timeline include the complexity of the case, completeness of documentation, and current processing volumes at U.S. Customs and Border Protection."
  },
  {
    question: "What documents are required?",
    answer: "Required documents typically include: criminal record documentation from all countries of residence, court documents, police clearance certificates, character references, employment verification, and a detailed statement of purpose for travel."
  },
  {
    question: "Is approval guaranteed?",
    answer: "No application outcome is guaranteed. U.S. Customs and Border Protection makes all approval decisions based on their assessment criteria. Canadian Document Services provides administrative preparation services only."
  }
];

const inadmissibilityReasons = [
  "DUI or impaired driving convictions",
  "Drug-related offences",
  "Assault or violent crime convictions",
  "Theft or fraud convictions",
  "Multiple criminal convictions",
  "Immigration violations"
];

const processSteps = [
  { number: 1, title: "Eligibility Assessment", description: "Review of your inadmissibility status and waiver requirements." },
  { number: 2, title: "Document Assembly", description: "Collection of criminal records, court documents, and supporting materials." },
  { number: 3, title: "Application Preparation", description: "Complete I-192 waiver application package prepared and reviewed." },
  { number: 4, title: "Submission & Follow-up", description: "Application submitted to CBP with ongoing status monitoring." }
];

// FAQ data for schema
const faqSchemaData = faqs.map(f => ({
  question: f.question,
  answer: f.answer,
}));

export default function USEntryWaivers() {
  return (
    <>
      <SEOHead 
        page="usEntryWaivers" 
        faqSchema={faqSchemaData}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'U.S. Entry Waivers', url: '/us-entry-waivers' },
        ]}
      />
      
      <div data-testid="us-entry-waivers-page">
        {/* Hero Section */}
        <section className="bg-[hsl(215,50%,23%)] text-white py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <p className="text-white/70 text-sm uppercase tracking-wide mb-2">U.S. Customs and Border Protection</p>
              <h1 className="text-3xl md:text-4xl font-bold mb-4 text-white">U.S. Entry Waivers</h1>
              <p className="text-white/80 text-lg mb-6">
                Administrative preparation for U.S. Entry Waiver applications (Form I-192) 
                for individuals deemed inadmissible to the United States due to criminal 
                history or other grounds.
              </p>
              <Link to="/eligibility">
                <Button className="bg-white text-[hsl(215,50%,23%)] hover:bg-gray-100 font-semibold px-6 py-3 rounded-sm" data-testid="ew-cta-btn">
                  Check Eligibility
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Service Overview - Plain language for AI search */}
        <section className="bg-[hsl(45,30%,96%)] border-b border-gray-200 py-6">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="font-bold text-[hsl(215,50%,23%)] mb-3">Service Overview</h2>
            <p className="text-gray-700">
              A U.S. Entry Waiver (Form I-192) allows individuals who have been deemed inadmissible to the United States 
              to legally enter the country for temporary purposes. Canadian Document Services provides administrative 
              assistance with the waiver application process, including document collection, application preparation, 
              and submission coordination. We are not a law firm and do not provide legal advice.
            </p>
          </div>
        </section>

        {/* Overview */}
        <section className="gov-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="gov-section-title">Understanding Inadmissibility</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Under U.S. immigration law, individuals with certain criminal convictions 
                  or other grounds are considered "inadmissible" and may be denied entry 
                  at the border, even for short visits.
                </p>
                <p>
                  A U.S. Entry Waiver (officially known as Form I-192) is a document that 
                  allows inadmissible individuals to temporarily enter the United States 
                  despite their inadmissibility status.
                </p>
              </div>
            </div>
            
            <div className="gov-panel">
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-4 text-lg">Common Grounds for Inadmissibility</h3>
              <div className="space-y-3">
                {inadmissibilityReasons.map((reason, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                    <span className="text-gray-700">{reason}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="gov-section-alt">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-12">Waiver Application Process</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="gov-panel"
                data-testid={`ew-process-step-${index + 1}`}
              >
                <div className="w-10 h-10 rounded-full bg-[hsl(215,50%,23%)] text-white flex items-center justify-center font-bold mb-4">
                  {step.number}
                </div>
                <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Information */}
      <section className="gov-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="gov-panel text-center">
              <Clock className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Processing Time</h3>
              <p className="text-gray-600 text-sm">
                Typically 6-18 months depending on case complexity and CBP processing volumes.
              </p>
            </div>
            
            <div className="gov-panel text-center">
              <Globe className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Waiver Validity</h3>
              <p className="text-gray-600 text-sm">
                Waivers are typically granted for 1-5 years and must be renewed before expiration.
              </p>
            </div>
            
            <div className="gov-panel text-center">
              <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Multiple Entry</h3>
              <p className="text-gray-600 text-sm">
                Once approved, the waiver allows multiple entries during the validity period.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-800 mb-2">Important Notice</h3>
                <p className="text-amber-700 text-sm">
                  Canadian Document Services Inc. is not affiliated with U.S. Customs and 
                  Border Protection or any U.S. government agency. All approval decisions 
                  are made exclusively by U.S. CBP. We provide administrative preparation 
                  services only and do not guarantee any specific outcome.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="gov-section-alt" data-testid="ew-faq-section">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-12">Frequently Asked Questions</h2>
          
          <div className="max-w-3xl mx-auto">
            <Accordion type="single" collapsible className="space-y-2">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`faq-${index}`}
                  className="gov-panel border border-gray-200"
                >
                  <AccordionTrigger className="text-left text-[hsl(215,50%,23%)] hover:no-underline px-0 font-semibold">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-600 pt-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="gov-section">
        <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
          <Globe className="h-12 w-12 text-[hsl(215,50%,23%)] mx-auto mb-6" />
          <h2 className="gov-section-title">Need to Travel to the United States?</h2>
          <p className="gov-subtitle mb-8">
            Start with a free eligibility assessment to determine your waiver requirements.
          </p>
          <Link to="/eligibility">
            <Button className="gov-btn-primary" data-testid="ew-bottom-cta-btn">
              Start Eligibility Assessment
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Trust & Compliance Section */}
      <section className="gov-section-alt">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="gov-section-title text-center mb-8">Trust & Compliance</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <Shield className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">CBP Requirements</h3>
              <p className="text-gray-600 text-sm">Applications prepared following U.S. Customs and Border Protection guidelines.</p>
            </div>
            <div className="text-center p-6">
              <Clock className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Status Tracking</h3>
              <p className="text-gray-600 text-sm">Ongoing monitoring and updates throughout the application process.</p>
            </div>
            <div className="text-center p-6">
              <Users className="h-8 w-8 text-[hsl(215,50%,23%)] mx-auto mb-4" />
              <h3 className="font-bold text-[hsl(215,50%,23%)] mb-2">Document Review</h3>
              <p className="text-gray-600 text-sm">Every application reviewed for completeness before submission.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="bg-white py-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <h2 className="text-lg font-bold text-[hsl(215,50%,23%)] mb-4">Related Services</h2>
          <div className="flex flex-wrap gap-4">
            <Link to="/record-suspensions" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              Record Suspension →
            </Link>
            <Link to="/international-filings" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              International Filings →
            </Link>
            <Link to="/contact" className="text-[hsl(210,80%,40%)] hover:underline text-sm">
              Contact Us →
            </Link>
          </div>
        </div>
      </section>
      </div>
    </>
  );
}
